/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type {
  LearnerProfile_Occupation as LearnerProfileOccupation,
  LearnerProfile_YearMonthDate as ProfileDate,
  LearnerProfile_WorkHistory as WorkHistory,
} from '__generated__/graphql-types-do-not-use';

import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';

import { Divider, Grid, IconButton, Skeleton, Typography2 } from '@coursera/cds-core';

import { getMonths } from 'bundles/account-profile/common/constants';
import { getOccupationRoleName } from 'bundles/account-profile/common/utils';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const styles = {
  workTitle: () => css`
    margin-right: var(--cds-spacing-100);
  `,
  header: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
  divider: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
};

type Props = {
  employerName?: string | null;
  jobTitle?: LearnerProfileOccupation | null;
  description?: string | null;
  startDate?: ProfileDate | null;
  endDate?: ProfileDate | null;
  isCurrentlyEmployed?: boolean | null;
  hasTopBorder?: boolean;
  workHistoryItem?: WorkHistory;
  handleOpenWorkHistoryModal: (workHistoryItem?: WorkHistory) => void;
};

export const WorkHistoryItemPlaceHolder = () => {
  return (
    <div role="status">
      <Typography2 variant="subtitleMedium" component="h4">
        <Skeleton />
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Typography2>
    </div>
  );
};

export const WorkHistoryItem = ({ workHistoryItem, handleOpenWorkHistoryModal, hasTopBorder }: Props) => {
  const { idToRolesMap } = useContext(OccupationRolesContext);
  const { editable } = useContext(ProfileContext);

  if (!workHistoryItem) return null;
  const { employerName, jobTitle, description, startDate, endDate, isCurrentlyEmployed } = workHistoryItem;

  const roleName = getOccupationRoleName(idToRolesMap, jobTitle);
  const monthsArray = getMonths();
  const formatWorkHistoryDate = () => {
    if (!startDate?.month && !startDate?.year) {
      return '';
    } else if ((!endDate?.month && !endDate?.year) || isCurrentlyEmployed) {
      return (
        <FormattedMessage
          message={_t('({startDate} - Present)')}
          startDate={`${monthsArray[startDate.month - 1]} ${startDate.year}`}
        />
      );
    } else {
      return (
        <FormattedMessage
          message={_t('({startDate} - {endDate})')}
          startDate={`${monthsArray[startDate.month - 1]} ${startDate.year}`}
          endDate={`${monthsArray[endDate.month - 1]} ${endDate.year}`}
        />
      );
    }
  };

  return (
    <div data-testid="work-history-item">
      {hasTopBorder && <Divider css={styles.divider} data-testid="work-item-divider" />}
      <Grid container direction="row" css={styles.header} alignItems="center" justifyContent="space-between">
        <Grid container item direction="row" alignItems="center" xs={8} sm={9} md={10} lg={11}>
          <Typography2 variant="subtitleMedium" css={styles.workTitle} component="h4">
            <FormattedHTMLMessage
              message={_t('{role} at {employerName}')}
              role={roleName}
              employerName={<span className="fs-mask">{employerName}</span>}
            />
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {formatWorkHistoryDate()}
          </Typography2>
        </Grid>
        {editable && (
          <IconButton
            intent="edit"
            aria-label={_t('Edit details for #{roleName} at #{employerName}.', { roleName, employerName })}
            variant="ghost"
            onClick={() => handleOpenWorkHistoryModal(workHistoryItem)}
            data-testid="work-history-item-edit-button"
            component={TrackedButton}
            // not tracked in eventing v3
            trackingName="profile_edit_work_history"
            requireFullyVisible={false}
            withVisibilityTracking={true}
          />
        )}
      </Grid>
      {description && (
        <Typography2 component="p" variant="bodyPrimary" color="supportText">
          {description}
        </Typography2>
      )}
    </div>
  );
};

export default WorkHistoryItem;
